import React, { useState, type FC, type KeyboardEvent } from "react";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { Input, Modal } from "../../../shared";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { formatString } from "../../../../utils/constants/stringUtils";
import styles from "../stylesDynamicBonuses.module.css";

type Props = {
  open: boolean;
  resourceType: "campaign" | "series" | "template";
  resourceName: string;
  isPending: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

const DeleteDynamicBonusModal: FC<Props> = ({
  open,
  resourceType,
  resourceName,
  isPending,
  onClose,
  onConfirm,
}) => {
  const [confirmation, setConfirmation] = useState("");
  const isExactMatch = confirmation === resourceName;
  const title =
    resourceType === "campaign"
      ? AppStrings.PAM_DYNAMIC_BONUS_DELETE_CAMPAIGN_TITLE
      : resourceType === "series"
        ? AppStrings.PAM_DYNAMIC_BONUS_DELETE_SERIES_TITLE
        : AppStrings.PAM_DYNAMIC_BONUS_DELETE_TEMPLATE_TITLE;

  const handleClose = () => {
    if (isPending) return;
    setConfirmation("");
    onClose();
  };

  const handleConfirm = () => {
    if (isExactMatch && !isPending) {
      onConfirm();
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      handleConfirm();
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size="sm"
      intent="destructive"
      title={title}
      primaryAction={{
        label: AppStrings.PAM_DYNAMIC_BONUS_DELETE_ACTION,
        onClick: handleConfirm,
        loading: isPending,
        disabled: !isExactMatch || isPending,
      }}
      secondaryAction={{
        label: AppStrings.PAM_DYNAMIC_BONUS_DELETE_CANCEL,
        onClick: handleClose,
        disabled: isPending,
      }}
    >
      <div className={styles.deleteConfirmationWarning} role="alert">
        <ExclamationCircleFilled aria-hidden="true" />
        <span>{AppStrings.PAM_DYNAMIC_BONUS_DELETE_WARNING}</span>
      </div>
      <p className={styles.deleteConfirmationInstruction}>
        {formatString(
          AppStrings.PAM_DYNAMIC_BONUS_DELETE_CONFIRMATION_INSTRUCTION,
          { name: resourceName },
        )}
      </p>
      <label
        className={styles.deleteConfirmationLabel}
        htmlFor="dynamic-bonus-delete-confirmation"
      >
        {AppStrings.PAM_DYNAMIC_BONUS_DELETE_CONFIRMATION_LABEL}
      </label>
      <Input
        id="dynamic-bonus-delete-confirmation"
        autoFocus
        value={confirmation}
        disabled={isPending}
        onChange={(event) => setConfirmation(event.target.value)}
        onKeyDown={handleKeyDown}
      />
    </Modal>
  );
};

export default DeleteDynamicBonusModal;
